import type { ProfileType } from "store/types/profile";
import type { SessionUser } from "types/session-user";

type SessionUserSource = ProfileType & Partial<SessionUser>;

const toIsoString = (value: Date | string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  return value instanceof Date ? value.toISOString() : value;
};

export const toSessionUser = (user: ProfileType): SessionUser => {
  const source = user as SessionUserSource;
  const oneTimePurchases = source.one_time_purchases;

  return {
    user_id: source.user_id,
    email: source.email,
    first_name: source.first_name,
    last_name: source.last_name,
    profile_image_path: source.profile_image_path ?? null,
    verified: source.verified,
    is_active: source.is_active,
    mfa_enabled: source.mfa_enabled,
    account_status: source.account_status ?? (source.is_active ? "active" : "inactive"),
    signup_method: source.signup_method,
    oauth2_sso_config_id: source.oauth2_sso_config_id,
    marketing_opt_in: source.marketing_opt_in ?? false,
    created_at: source.created_at,
    last_login_at: source.last_login_at ?? null,
    updated_at: source.updated_at,
    pricing_plan_id: source.pricing_plan_id,
    pricing_plan: source.pricing_plan
      ? {
          pricing_plan_id: source.pricing_plan.pricing_plan_id,
          plan_name: source.pricing_plan.plan_name,
          max_summary_revisions: source.pricing_plan.max_summary_revisions,
          max_projects: source.pricing_plan.max_projects,
          max_seats: source.pricing_plan.max_seats,
          max_schedule_reruns: source.pricing_plan.max_schedule_reruns,
          max_budget_reruns: source.pricing_plan.max_budget_reruns,
          price_per_month: source.pricing_plan.price_per_month,
          price_per_year: source.pricing_plan.price_per_year,
          per_user_price: source.pricing_plan.per_user_price,
          per_project_price: source.pricing_plan.per_project_price,
        }
      : null,
    subscription: source.subscription ?? null,
    upcoming_pricing_plan: source.upcoming_pricing_plan ?? null,
    one_time_purchases: oneTimePurchases
      ? {
          ...oneTimePurchases,
          latest_purchase_date: toIsoString(oneTimePurchases.latest_purchase_date),
          trial_expiry_date: toIsoString(oneTimePurchases.trial_expiry_date),
        }
      : null,
    billing_model:
      source.billing_model ??
      (source.is_one_time_purchase || source.isOneTimePurchase ? "one_time" : "subscription"),
    is_one_time_purchase: source.is_one_time_purchase ?? source.isOneTimePurchase ?? false,
    posthog_identity_hash: source.posthog_identity_hash ?? "",
  };
};
